import React, { useState, useCallback, useRef, useMemo } from "react";
import createDealTokenExtension from "./codemirror/dealTokenExtension";
import createEnumTokenExtension from "./codemirror/enumTokenExtension";
import createDateTokenExtension from "./codemirror/dateTokenExtension";
import createProductTokenExtension from "./codemirror/productTokenExtension";
import functionExtension, {
  functionHighlightTheme,
} from "./codemirror/functionExtension";
import {
  createFunctionTooltipField,
  functionTooltipTheme,
  clearTooltipsEffect,
} from "./codemirror/functionTooltipExtension";
import { theme, attributes } from "./codemirror/theme";
import functions from "./codemirror/functions";
import dateTokenOptions from "./codemirror/dateTokens";
import { EditorView } from "@codemirror/view";

import CodeMirror from "@uiw/react-codemirror";
import {
  autocompletion,
  completeFromList,
  startCompletion,
  insertCompletionText,
} from "@codemirror/autocomplete";

import InsertTokenFunction from "./fields/InsertTokenFunction";
import { TrashIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid";

import classNames from "../utils/classnames";

export default function LineItemPropertyInput({
  propertyDefinition,
  dealPropertyDefinitions,
  productPropertyDefinitions,
  data,
  previewError,
  updateProperty,
  removeProperty,
  required,
  hubspotPortal,
  isOverwritten,
}) {
  const isEnumInput = propertyDefinition.type === "enumeration";
  const isMultiSelectEnumInput =
    isEnumInput && propertyDefinition.fieldType === "checkbox";

  let initialValue = data.value.toString();

  // "option" is a legacy data type for enumerations.
  // This code is required so that the value will display correctly as an atomised enumeration.
  if (data.type === "option") {
    initialValue = `{{enum.${initialValue}}}`;
  }

  const editorRef = useRef();

  const [value, setValue] = useState(initialValue);

  const [isFocused, setIsFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dealTokenExtension = useMemo(() => {
    return createDealTokenExtension(dealPropertyDefinitions);
  }, [dealPropertyDefinitions]);

  const dateTokenExtension = useMemo(() => {
    return createDateTokenExtension(dateTokenOptions);
  }, [dateTokenOptions]);

  const productTokenExtension = useMemo(() => {
    return createProductTokenExtension(productPropertyDefinitions);
  }, [productPropertyDefinitions]);

  let enumTokenExtension, autocompleteExtension;
  if (isEnumInput) {
    enumTokenExtension = useMemo(() => {
      return createEnumTokenExtension(propertyDefinition.options);
    }, [propertyDefinition]);

    const autocompleteOptions = propertyDefinition.options.map((o) => ({
      label: o.label,
      apply: (view, completion, from, to) => {
        const insert = insertCompletionText(
          view.state,
          `{{enum.${o.value}}}`,
          from,
          to,
        );

        view.dispatch(insert);
      },
    }));

    autocompleteExtension = autocompletion({
      override: [completeFromList(autocompleteOptions)],
      activateOnTypingDelay: 100,
    });
  }

  const onArgChange = () => {
    if (isEnumInput) {
      openEnumDropdown();
    }
  };

  const onArgStart = () => {
    if (isEnumInput) {
      openEnumDropdown();
    }
  };

  const functionTooltipField = useMemo(() => {
    return createFunctionTooltipField(onArgChange, onArgStart);
  }, []);

  const dynamicAttributes = useMemo(() => {
    return EditorView.editorAttributes.of({
      class:
        isFocused || isDropdownOpen
          ? "border-transparent outline-none ring-2 ring-primary"
          : "",
    });
  }, [isFocused, isDropdownOpen]);

  const openEnumDropdown = () => {
    setTimeout(() => {
      startCompletion(editorRef.current.view);
    }, 100);
  };

  const onChange = useCallback((val) => {
    setValue(val);

    updateProperty(propertyDefinition.name, val, "calculation");

    if (
      (val === "" && isEnumInput) ||
      (val.slice(-1) === ";" && isMultiSelectEnumInput)
    ) {
      openEnumDropdown();
    }
  }, []);

  const handleFocus = useCallback(() => {
    setIsFocused(true);

    if (isEnumInput) {
      openEnumDropdown();
    }
  }, []);

  const handleBlur = useCallback(() => {
    editorRef.current.view.dispatch({ effects: clearTooltipsEffect.of(null) });
    // Delay required, to prevent a styling flash when we are swapping between
    // The Code Mirror input being focused, and the dropdown being open.
    setTimeout(() => setIsFocused(false), 150);
  }, []);

  const handleTokenSelect = (selectedOption, tokenType) => {
    if (!selectedOption) {
      return;
    }
    const token = `{{${tokenType}.${selectedOption.value}}}`;
    const cursorPos = editorRef.current?.view?.state?.selection.main.head;
    editorRef.current.view.focus();
    editorRef.current.view.dispatch({
      changes: {
        from: cursorPos,
        insert: token,
      },
      selection: {
        anchor: cursorPos + token.length,
        head: cursorPos + token.length,
      },
    });
  };

  const handleFunctionSelect = (selectedOption) => {
    if (!selectedOption) {
      return;
    }

    const cursorPos = editorRef.current?.view?.state?.selection.main.head;
    editorRef.current.view.focus();

    editorRef.current.view.dispatch({
      changes: {
        from: cursorPos,
        insert: `${selectedOption.value}()`,
      },
      selection: {
        anchor: cursorPos + selectedOption.value.length + 1,
        head: cursorPos + selectedOption.value.length + 1,
      },
    });
  };

  const pinnedDealProperties = ["dealname", "closedate", "dealstage", "amount"];

  const dealPropertyOptions = [
    // Add pinned options in order, only if present in definitions
    ...pinnedDealProperties
      .map((name) => dealPropertyDefinitions.find((p) => p.name === name))
      .filter(Boolean)
      .map((p) => ({
        label: p.label,
        value: p.name,
      })),
    // Add the rest, sorted
    ...dealPropertyDefinitions
      .filter((p) => !pinnedDealProperties.includes(p.name))
      .map((p) => ({
        label: p.label,
        value: p.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const pinnedProductProperties = [
    "name",
    "sku",
    "price",
    "description",
    "hs_cost_of_goods_sold",
    "recurringbillingfrequency",
    "hs_recurring_billing_period",
  ];

  let productPropertyOptions;
  if (productPropertyDefinitions) {
    productPropertyOptions = [
      // Add pinned options in order, only if present in definitions
      ...pinnedProductProperties
        .map((name) => productPropertyDefinitions.find((p) => p.name === name))
        .filter(Boolean)
        .map((p) => ({
          label: p.label,
          value: p.name,
        })),
      // Add the rest, sorted
      ...productPropertyDefinitions
        .filter((p) => !pinnedProductProperties.includes(p.name))
        .map((p) => ({
          label: p.label,
          value: p.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }

  const extensions = [
    theme,
    attributes,
    dynamicAttributes,
    dealTokenExtension,
    dateTokenExtension,
    productTokenExtension,
    functionExtension,
    functionHighlightTheme,
    EditorView.lineWrapping,
    functionTooltipField,
    functionTooltipTheme,
  ];

  if (isEnumInput) {
    extensions.push(autocompleteExtension);
    extensions.push(enumTokenExtension);
  }

  return (
    <div className="group/line-item-property-input">
      <div className="">
        <label className="flex items-center w-full gap-3 text-sm line-item-property-label">
          <div className={classNames("flex-1 semibold")}>
            {propertyDefinition?.label}{" "}
          </div>

          {!required && (
            <div className="flex flex-shrink">
              <button className="text-xs button-text hover:text-red-600">
                <TrashIcon
                  onClick={() => removeProperty(propertyDefinition.name)}
                  className="w-3 h-3"
                />
              </button>
            </div>
          )}
        </label>
      </div>
      <div className="flex">
        <div className="relative flex-1">
          <CodeMirror
            ref={editorRef}
            value={value}
            extensions={extensions}
            basicSetup={{
              lineNumbers: false,
              foldGutter: false,
            }}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />

          <InsertTokenFunction
            dealPropertyOptions={dealPropertyOptions}
            productPropertyOptions={productPropertyOptions}
            functionOptions={functions}
            dateTokenOptions={dateTokenOptions}
            handleTokenSelect={handleTokenSelect}
            handleFunctionSelect={handleFunctionSelect}
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            hubspotPortal={hubspotPortal}
            isFocused={isFocused}
          />
        </div>
      </div>
      {isOverwritten && (
        <div className="flex-shrink mt-1 text-xs text-tone-700">
          Note: This will override the value set in your product library
        </div>
      )}
      {propertyDefinition.fieldType === "checkbox" && (
        <div className="flex-shrink mt-1 text-xs text-tone-700">
          Note: Separate multiple values with a semicolon (;)
        </div>
      )}
      {propertyDefinition.type === "date" && (
        <div className="flex-shrink mt-1 text-xs text-tone-700">
          Note: Dates should be in the format YYYY-MM-DD, e.g. 2023-10-01
        </div>
      )}
      {propertyDefinition.type === "datetime" && (
        <div className="flex-shrink mt-1 text-xs text-tone-700">
          Note: Dates and Times should be in the format YYYY-MM-DD or
          YYYY-MM-DDTHH:mm:ss, e.g. 2023-10-01T12:00:00
        </div>
      )}
      {previewError && (
        <div className="flex items-center flex-shrink gap-2 mt-1 text-xs text-red-700">
          <ExclamationTriangleIcon
            className="w-5 h-5 text-red-700"
            aria-hidden="true"
          />
          {previewError.error_type === "validationError"
            ? `Recent error on preview: The calculated value wasn't a valid ${previewError.type} (value was "${previewError.value}")`
            : previewError.message}
        </div>
      )}
    </div>
  );
}
